"use client";

import { Section, Tire, Undercarriages } from "@/@types";
import { useMemo, useState } from "react";
import UsedProductContitionsCategoryTabs from "./UsedProductContitionsCategoryTabs";
import UsedProductCategoryContent from "./UsedProductCategoryContent";

type Props = {
    sections: Section[],
    undercarriages?: Undercarriages[],
    tires?: Tire[],
}

const classificationMap: Record<string, { label: string; color: string }> = {
    "1": { label: "BUENO", color: "bg-green-500" },
    "2": { label: "SUFICIENTE", color: "bg-yellow-500" },
    "3": { label: "DEFICIENTE", color: "bg-red-500" },
};

const categoryTranslations: Record<string, string> = {
    "SAFETY ITEMS": "ELEMENTOS DE SEGURIDAD",
    "OPERATOR STATION": "ESTACIÓN DEL OPERADOR",
};

export default function UsedProductConditions({ sections, undercarriages, tires }: Props) {
    const groupedSections = useMemo(() => {
        const grouped: Record<string, any> = sections.reduce(( acc: Record<string, any>, section: Section ) => {
            const originalCategoryName = section.category?.name;

            if (!originalCategoryName) return acc;

            const categoryName = 
                categoryTranslations[originalCategoryName.toUpperCase()] ?? 
                originalCategoryName.toUpperCase();

            if (!acc[categoryName]) {
                acc[categoryName] = {
                    booleanFeatures: [],
                    discreteFeatures: [],
                    noteFeatures: [],
                    sectionNotes: [],
                }
            }

            if (section.booleanFeatures) {
                acc[categoryName].booleanFeatures.push(...section.booleanFeatures);
            }

            if (section.discreteFeatures) {
                acc[categoryName].discreteFeatures.push(...section.discreteFeatures);
            }

            if (section.noteFeatures) {
                acc[categoryName].noteFeatures.push(...section.noteFeatures);
            }

            if (section.sectionNotes) {
                acc[categoryName].sectionNotes.push(...section.sectionNotes);
            }

            return acc;
        }, {});

        Object.keys(grouped).forEach((key) => {
            const hasBoolean = grouped[key].booleanFeatures.length > 0;
            const hasDiscrete = grouped[key].discreteFeatures.length > 0;
            const hasNotes = grouped[key].noteFeatures.length > 0;
            const hasSectionNotes = grouped[key].sectionNotes.length > 0;
            const hasTireNotes = tires && tires.length > 0;
            const hasUndercarriageNotes = undercarriages && undercarriages.length > 0;

            if (
                !hasBoolean &&
                !hasDiscrete &&
                !hasNotes &&
                !hasSectionNotes &&
                !hasTireNotes &&
                !hasUndercarriageNotes
            ) {
                delete grouped[key];
            }
        })

        return grouped;
    }, [sections]);

    const categories = Object.keys(groupedSections).sort((a, b) => a.localeCompare( b, "es" ));

    const dynamicCategories = [...categories];

    const categoriesToRender = dynamicCategories.sort((a, b) =>
        a.localeCompare(b, "es")
    );

    const [activeCategory, setActiveCategory] = useState<string | null>(
        categoriesToRender.length > 0 ? categoriesToRender[0] : null
    );

    return (
        <div>
            <div className="flex items-center justify-evenly gap-6 mb-6">
                <span className="font-bold">CLASIFICACIÓN</span>
                {Object.values(classificationMap).map((item) => (
                    <div className="flex items-center gap-2" key={item.label}>
                        <span className={`w-4 h-4 ${item.color} rounded-full`}></span>
                        <span className="text-sm hidden md:block">{item.label}</span>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div>
                    <UsedProductContitionsCategoryTabs
                        categories={categoriesToRender}
                        activeCategory={activeCategory}
                        onChange={setActiveCategory}
                        renderMobileContent={(category) => (
                            <UsedProductCategoryContent
                            activeCategory={category}
                            groupedSections={groupedSections}
                            undercarriages={undercarriages}
                            tires={tires}
                            classificationMap={classificationMap}
                            isMobile
                            />
                        )}
                    />
                </div>

                <div className="lg:col-span-3">
                    <UsedProductCategoryContent
                        activeCategory={activeCategory}
                        groupedSections={groupedSections}
                        undercarriages={undercarriages}
                        tires={tires}
                        classificationMap={classificationMap}
                    />
                </div>
            </div>
        </div>
    )
}